/** Build OR expression so list pages stay accessible when only add/edit/delete is granted. */
export const listPageAccess = function () {
  const permissionCodes = Array.prototype.slice.call(arguments);
  return permissionCodes.filter(Boolean).join("||");
};

/**
 * Widen list-route access to include add/edit/delete siblings so removing
 * action permissions does not hide the whole list page.
 */
export const enhanceRoutesWithCrudAccess = (routeList) => {
  if (!Array.isArray(routeList)) {
    return routeList;
  }

  const addByBase = {};
  const editByBase = {};

  for (let i = 0; i < routeList.length; i += 1) {
    const route = routeList[i];
    const name = route && route.name;
    const action = route && route.action;
    if (!name || !action) {
      continue;
    }

    if (name.endsWith("/add")) {
      addByBase[name.slice(0, -4)] = action;
      continue;
    }

    if (name.endsWith("/edit")) {
      editByBase[name.slice(0, -5)] = action;
    }
  }

  const enhancedPaths = {};

  return routeList.map((route) => {
    const name = route && route.name;
    if (!name || enhancedPaths[name]) {
      return route;
    }

    const addAction = addByBase[name];
    const editAction = editByBase[name];
    if (!addAction && !editAction) {
      return route;
    }

    enhancedPaths[name] = true;

    let deleteAction = null;
    if (typeof editAction === "string") {
      deleteAction = editAction.replace(/_EDIT$/, "_DELETE");
    }

    return {
      ...route,
      action: listPageAccess(
        route.action,
        addAction,
        editAction,
        deleteAction !== editAction ? deleteAction : null
      ),
    };
  });
};

/** Returns true when the user has the given permission action. */
export const hasPermission = (
  permissions,
  action,
  isPermissionLoaded = true
) => {
  if (!action) {
    return true;
  }
  if (!isPermissionLoaded) {
    return false;
  }
  if (!Array.isArray(permissions) || permissions.length === 0) {
    return false;
  }

  const tokens = String(action)
    .split("||")
    .map((token) => String(token).trim().toUpperCase())
    .filter(Boolean);

  if (!tokens.length) {
    return false;
  }

  return tokens.some((token) =>
    permissions.some(
      (permission) => String(permission).trim().toUpperCase() === token
    )
  );
};

/**
 * Resolve edit/delete permissions from edit_delete_action array.
 * Supports both [EDIT, DELETE] and [DELETE, EDIT] order by matching suffix.
 */
export const resolveEditDeletePermissions = (
  permissions,
  editDeleteAction,
  isPermissionLoaded = true
) => {
  if (
    !editDeleteAction ||
    !Array.isArray(editDeleteAction) ||
    editDeleteAction.length === 0
  ) {
    return {
      canEdit: false,
      canDelete: false,
      editPermission: null,
      deletePermission: null,
    };
  }

  let editPermission = null;
  for (let i = 0; i < editDeleteAction.length; i += 1) {
    const action = editDeleteAction[i];
    if (typeof action !== "string") continue;
    const tokens = action.split("||").map((t) => t.trim());
    if (tokens.some((token) => token.endsWith("_EDIT"))) {
      editPermission = action;
      break;
    }
  }
  if (!editPermission) {
    editPermission = editDeleteAction[0] || null;
  }

  let deletePermission = null;
  for (let i = 0; i < editDeleteAction.length; i += 1) {
    const action = editDeleteAction[i];
    if (typeof action !== "string") continue;
    const tokens = action.split("||").map((t) => t.trim());
    if (tokens.some((token) => token.endsWith("_DELETE"))) {
      deletePermission = action;
      break;
    }
  }
  if (!deletePermission) {
    deletePermission = editDeleteAction[1] || null;
  }

  return {
    editPermission,
    deletePermission,
    canEdit: hasPermission(permissions, editPermission, isPermissionLoaded),
    canDelete: hasPermission(permissions, deletePermission, isPermissionLoaded),
  };
};
